import { normalizeContractStatus } from '../utils/contractStatusUtils';
import { EContractStatus } from '../services/contracts/ContractService';
import {
  CampaignKind,
  CampaignPeriodMode,
  CampaignResponseDto,
  CampaignScheduleBlock,
  CampaignScheduleDay,
  CampaignScheduleDocument,
  CampaignEligibilityProfileDto,
  CampaignEligibilityRulesDto,
  CommissionTiersDocument,
  ScoringBandRule,
  ScoringTableDocument,
  UpdateCampaignEligibilityProfileDto,
  UpdateCampaignEligibilityRulesDto,
  WeeklyBonusTier,
  CampaignWeeklyWindow,
  CampaignWeeklyWindowsDocument,
} from '../types/campaigns.types';

/** Receita aprovada (metas) — snapshot Agosto-2026. */
export const DEFAULT_CAMPAIGN_META_APPROVED_STATUSES: number[] = [
  EContractStatus.Ativo,
  EContractStatus.ValidandoDocumentacao,
  EContractStatus.AguardandoEmissaoNF,
  EContractStatus.AguardandoEntrega,
  EContractStatus.ProcessoDeAtivacao,
  EContractStatus.BateVolta,
  EContractStatus.Finalizado,
  EContractStatus.ProvisionadoAguardandoEntrega,
  EContractStatus.ProcessoAtivacaoPortabilidades,
  EContractStatus.ErroPortabilidade,
];

/** Receita em aprovação (metas) — snapshot Agosto-2026. */
export const DEFAULT_CAMPAIGN_META_EM_APROVACAO_STATUSES: number[] = [
  EContractStatus.AguardandoAnaliseAntiFraude,
  EContractStatus.AnaliseHolding,
  EContractStatus.AnaliseSupervisor,
  EContractStatus.CVP,
  EContractStatus.Input,
  EContractStatus.AguardandoCI,
  EContractStatus.EmAnaliseBackOffice,
  EContractStatus.AprovadoCQV,
  EContractStatus.EmAnalise,
  EContractStatus.ReanaliseCredito,
];

/** Receita aprovada (pontos) — snapshot Agosto-2026. */
export const DEFAULT_CAMPAIGN_PONTOS_APPROVED_STATUSES: number[] = [
  EContractStatus.Ativo,
  EContractStatus.ValidandoDocumentacao,
  EContractStatus.AguardandoEmissaoNF,
  EContractStatus.AguardandoEntrega,
  EContractStatus.ProcessoDeAtivacao,
  EContractStatus.Finalizado,
  EContractStatus.ProvisionadoAguardandoEntrega,
  EContractStatus.ProcessoAtivacaoPortabilidades,
  EContractStatus.ErroPortabilidade,
];

/** Receita em aprovação (pontos) — snapshot Agosto-2026. */
export const DEFAULT_CAMPAIGN_PONTOS_EM_APROVACAO_STATUSES: number[] = [
  EContractStatus.AguardandoAnaliseAntiFraude,
  EContractStatus.AguardandoCorrecaoConsultor,
  EContractStatus.AguardandoFIDEL,
  EContractStatus.AnaliseHolding,
  EContractStatus.AnaliseSupervisor,
  EContractStatus.CVP,
  EContractStatus.Input,
  EContractStatus.InsucessoEntrega,
  EContractStatus.ProcedimentoNAC,
];

export const DEFAULT_CAMPAIGN_ELIGIBILITY_APPROVED_STATUSES: number[] =
  DEFAULT_CAMPAIGN_META_APPROVED_STATUSES;

export const DEFAULT_CAMPAIGN_ELIGIBILITY_EM_APROVACAO_STATUSES: number[] =
  DEFAULT_CAMPAIGN_META_EM_APROVACAO_STATUSES;

/** @deprecated Use DEFAULT_CAMPAIGN_ELIGIBILITY_APPROVED_STATUSES */
export const DEFAULT_CAMPAIGN_ELIGIBILITY_CONTRACT_STATUSES: number[] =
  DEFAULT_CAMPAIGN_ELIGIBILITY_APPROVED_STATUSES;

export function normalizeAllowedContractStatuses(
  statuses: unknown[] | null | undefined
): number[] {
  if (!statuses?.length) return [];

  const normalized = statuses
    .map((status) => normalizeContractStatus(status as string | number))
    .filter((status): status is number => status !== null);

  return Array.from(new Set(normalized));
}

/** API serializa CampaignKind como string camelCase ou número. */
export function parseCampaignKind(kind: unknown): CampaignKind {
  if (kind === 2 || kind === '2' || kind === 'mista' || kind === 'Mista') {
    return 2;
  }
  if (kind === 1 || kind === '1' || kind === 'pontuacaoComissao' || kind === 'PontuacaoComissao') {
    return 1;
  }
  return 0;
}

export function parseCampaignPeriodMode(mode: unknown): CampaignPeriodMode {
  if (mode === 1 || mode === '1' || mode === 'periodo' || mode === 'Periodo') {
    return 1;
  }
  return 0;
}

export function normalizeCampaign(campaign: CampaignResponseDto): CampaignResponseDto {
  return {
    ...campaign,
    kind: parseCampaignKind(campaign.kind),
    periodMode: parseCampaignPeriodMode(campaign.periodMode ?? 0),
  };
}

/** Converte ISO da API para valor de input type="date" (data local). */
export function isoDateToInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Formata YYYY-MM-DD para exibição pt-BR sem deslocamento de fuso. */
export function formatDateInput(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
}

/** Vigência da campanha para filtros do dashboard (YYYY-MM-DD). */
export function getCampaignDateRange(campaign: CampaignResponseDto): { start: string; end: string } {
  const periodMode = parseCampaignPeriodMode(campaign.periodMode ?? 0);

  if (periodMode === 1 && campaign.dataInicio && campaign.dataFim) {
    return {
      start: isoDateToInput(campaign.dataInicio),
      end: isoDateToInput(campaign.dataFim),
    };
  }

  const [year, month] = (campaign.mesReferencia ?? '').split('-').map(Number);
  if (!year || !month) {
    return { start: '', end: '' };
  }

  const lastDay = new Date(year, month, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    start: `${year}-${pad(month)}-01`,
    end: `${year}-${pad(month)}-${pad(lastDay)}`,
  };
}

/** Rótulo de período para dropdown de campanha (nome — período). */
export function formatCampaignPeriodLabel(campaign: CampaignResponseDto): string {
  const { start, end } = getCampaignDateRange(campaign);
  const periodMode = parseCampaignPeriodMode(campaign.periodMode ?? 0);

  if (periodMode === 1 && start && end) {
    return `${formatDateInput(start)} — ${formatDateInput(end)}`;
  }

  return campaign.mesReferencia ?? '';
}

/** Tipos de linha baseados em valor do plano. */
export const PLAN_BASED_SCORING_LINE_TYPES = ['aditivo', 'portabilidade', 'novaLinha'] as const;

export const DEFAULT_PLAN_SCORING_LINE_TYPES: string[] = [...PLAN_BASED_SCORING_LINE_TYPES];

export const getScoringRuleTipos = (rule: ScoringBandRule): string[] => {
  if (rule.tiposLinha?.length) return rule.tiposLinha;
  if (rule.tipoLinha) return [rule.tipoLinha];
  return ['aditivo'];
};

export const isPlanBasedScoringRule = (rule: ScoringBandRule): boolean =>
  getScoringRuleTipos(rule).some((t) =>
    (PLAN_BASED_SCORING_LINE_TYPES as readonly string[]).includes(t)
  );

/** Tabela Kaio/Davi padrão (referência do sistema). */
export const DEFAULT_SCORING_TABLE: ScoringTableDocument = {
  renegociacaoAptaPontos: 0.5,
  faixasPlano: [
    { tiposLinha: ['renegociacaoApta'], pontos: 0.5 },
    {
      tiposLinha: DEFAULT_PLAN_SCORING_LINE_TYPES,
      maxValorComPermanencia: 29.99,
      pontos: 0.5,
    },
    {
      tiposLinha: DEFAULT_PLAN_SCORING_LINE_TYPES,
      maxValorComPermanencia: 89.99,
      pontos: 1,
    },
    {
      tiposLinha: DEFAULT_PLAN_SCORING_LINE_TYPES,
      maxValorComPermanencia: 99.99,
      pontos: 2,
    },
    {
      tiposLinha: DEFAULT_PLAN_SCORING_LINE_TYPES,
      maxValorComPermanencia: 149.99,
      pontos: 3,
    },
  ],
};

export const SCORING_LINE_TYPE_OPTIONS: { key: string; value: string; text: string }[] = [
  { key: 'aditivo', value: 'aditivo', text: 'Aditivo' },
  { key: 'portabilidade', value: 'portabilidade', text: 'Portabilidade' },
  { key: 'novaLinha', value: 'novaLinha', text: 'Nova linha' },
  { key: 'renegociacaoApta', value: 'renegociacaoApta', text: 'Renegociação apta' },
  { key: 'renegociacaoInapta', value: 'renegociacaoInapta', text: 'Renegociação inapta' },
];

export const normalizeScoringTable = (table: ScoringTableDocument): ScoringTableDocument => {
  const faixas: ScoringBandRule[] = table.faixasPlano.map((f) => {
    const tiposLinha = getScoringRuleTipos(f);
    const { tipoLinha: _legacy, ...rest } = f;
    return { ...rest, tiposLinha };
  });

  if (!faixas.some((f) => f.tiposLinha?.includes('renegociacaoApta'))) {
    faixas.unshift({ tiposLinha: ['renegociacaoApta'], pontos: table.renegociacaoAptaPontos });
  }

  return { ...table, faixasPlano: faixas };
};

export const cloneScoringTable = (table: ScoringTableDocument): ScoringTableDocument =>
  normalizeScoringTable({
    renegociacaoAptaPontos: table.renegociacaoAptaPontos,
    faixasPlano: table.faixasPlano.map((f) => ({ ...f })),
  });

/** Tabela de pontos Kaio/Davi (referência legível na UI). */
export const KAIO_SCORING_TABLE = [
  { tipo: 'Renegociação apta', condicao: 'IsApto = true', pontos: '0,5' },
  { tipo: 'Nova Linha / Aditivo / Portabilidade', condicao: 'Plano até R$ 29,99', pontos: '0,5' },
  { tipo: 'Nova Linha / Aditivo / Portabilidade', condicao: 'Plano até R$ 89,99', pontos: '1' },
  { tipo: 'Nova Linha / Aditivo / Portabilidade', condicao: 'Plano até R$ 99,99', pontos: '2' },
  { tipo: 'Nova Linha / Aditivo / Portabilidade', condicao: 'Plano até R$ 149,99', pontos: '3' },
] as const;

/** Tiers padrão (Agosto-2026): 1º mês 23/33/43/53 pts; 2º mês+ 30/40/55/83 pts (R$/ponto: 20/35/45/60). */
export const DEFAULT_COMMISSION_TIERS: CommissionTiersDocument = {
  segundoMes: [
    { tier: 1, minPontos: 30, minReceita: 1100, valorPorPonto: 20 },
    { tier: 2, minPontos: 40, minReceita: 1800, valorPorPonto: 35 },
    { tier: 3, minPontos: 55, minReceita: 2200, valorPorPonto: 45 },
    { tier: 4, minPontos: 83, minReceita: 3500, valorPorPonto: 60 },
  ],
  primeiroMes: [
    { tier: 1, minPontos: 23, minReceita: 800, valorPorPonto: 20 },
    { tier: 2, minPontos: 33, minReceita: 1100, valorPorPonto: 35 },
    { tier: 3, minPontos: 43, minReceita: 1500, valorPorPonto: 45 },
    { tier: 4, minPontos: 53, minReceita: 1700, valorPorPonto: 60 },
  ],
};

export const CAMPAIGN_KIND_OPTIONS = [
  { key: 0, value: 0, text: 'Metas por Linhas (equipes)' },
  { key: 1, value: 1, text: 'Pontuação e Comissão (vendedores)' },
  { key: 2, value: 2, text: 'Mista (metas + pontuação)' },
] as const;

export const CAMPAIGN_KIND_LABELS: Record<number, string> = {
  0: 'Metas Linhas',
  1: 'Pontuação/Comissão',
  2: 'Mista',
};

export const CAMPAIGN_PERIOD_MODE_OPTIONS = [
  { key: 0, value: 0, text: 'Mensal' },
  { key: 1, value: 1, text: 'Período customizado' },
] as const;

export const CAMPAIGN_PERIOD_MODE_LABELS: Record<number, string> = {
  0: 'Mensal',
  1: 'Período',
};

export const CAMPAIGN_TIPOS_CONTRATACAO_OPTIONS = [
  'Aditivo',
  'Renegociação',
  'Renegociacao',
  'Nova Linha',
  'Nova Linha Empresarial',
  '1º Venda',
  'Aditivo/Renegociação',
  'Aditivo/Renegociacao',
].map((tipo) => ({ key: tipo, value: tipo, text: tipo }));

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export const CAMPAIGN_SCHEDULE_DAY_LABELS = DAY_NAMES;

export function createDefaultScheduleBlock(): CampaignScheduleBlock {
  const days: CampaignScheduleDay[] = [];
  for (let d = 0; d <= 6; d++) {
    days.push({
      dayOfWeek: d,
      enabled: true,
      horaInicio: '00:00',
      horaFim: '23:59',
    });
  }
  return { days };
}

export function createDefaultSchedule(): CampaignScheduleDocument {
  return {
    timezone: 'America/Sao_Paulo',
    usarGradeSeparadaPontos: false,
    meta: createDefaultScheduleBlock(),
    pontos: null,
  };
}

export function cloneSchedule(schedule: CampaignScheduleDocument): CampaignScheduleDocument {
  const cloneBlock = (block: CampaignScheduleBlock): CampaignScheduleBlock => ({
    days: block.days.map((d) => ({ ...d })),
  });
  return {
    timezone: schedule.timezone ?? 'America/Sao_Paulo',
    usarGradeSeparadaPontos: schedule.usarGradeSeparadaPontos,
    meta: cloneBlock(schedule.meta),
    pontos: schedule.pontos ? cloneBlock(schedule.pontos) : null,
  };
}

export type EligibilityProfileBlock = 'meta' | 'pontos';

function sharedEligibilityStatusDefaults(
  block: EligibilityProfileBlock = 'meta'
): Omit<CampaignEligibilityProfileDto, 'incluirAditivo' | 'incluirRenegociacao'> {
  const approved =
    block === 'meta'
      ? DEFAULT_CAMPAIGN_META_APPROVED_STATUSES
      : DEFAULT_CAMPAIGN_PONTOS_APPROVED_STATUSES;
  const emAprovacao =
    block === 'meta'
      ? DEFAULT_CAMPAIGN_META_EM_APROVACAO_STATUSES
      : DEFAULT_CAMPAIGN_PONTOS_EM_APROVACAO_STATUSES;
  return {
    incluirRenegociacaoInapta: false,
    desconsiderarPortabilidade: false,
    desconsiderarNovaLinha: false,
    desconsiderarFastchip: false,
    desconsiderarPorEntrega: false,
    incluirLinhasSuspensas: false,
    incluirLinhasExcluidas: false,
    incluirRequerSuspensao: false,
    considerarSemDataAtivacao: true,
    desconsiderarAtivados: false,
    allowedContractStatuses: [...approved],
    statusesEmAprovacao: [...emAprovacao],
    planIds: [],
    desconsiderarPlanosSelecionados: false,
  };
}

/** Defaults do bloco Metas — apenas aditivo. */
export function defaultMetaEligibilityProfile(): CampaignEligibilityProfileDto {
  return {
    ...sharedEligibilityStatusDefaults('meta'),
    incluirAditivo: true,
    incluirRenegociacao: false,
  };
}

/** Defaults do bloco Pontuação — aditivo e renegociação. */
export function defaultPontosEligibilityProfile(): CampaignEligibilityProfileDto {
  return {
    ...sharedEligibilityStatusDefaults('pontos'),
    incluirAditivo: true,
    incluirRenegociacao: true,
  };
}

/** @deprecated Preferir defaultMetaEligibilityProfile ou defaultPontosEligibilityProfile. */
export function defaultEligibilityProfile(): CampaignEligibilityProfileDto {
  return defaultPontosEligibilityProfile();
}

export function normalizeEligibilityProfile(
  profile: Partial<CampaignEligibilityProfileDto> | null | undefined,
  block: EligibilityProfileBlock = 'pontos'
): UpdateCampaignEligibilityProfileDto {
  const base = block === 'meta' ? defaultMetaEligibilityProfile() : defaultPontosEligibilityProfile();
  if (!profile) return base;
  return {
    ...base,
    ...profile,
    allowedContractStatuses: normalizeAllowedContractStatuses(profile.allowedContractStatuses),
    incluirRenegociacaoInapta: profile.incluirRenegociacaoInapta ?? base.incluirRenegociacaoInapta,
    desconsiderarPortabilidade: profile.desconsiderarPortabilidade ?? base.desconsiderarPortabilidade,
    desconsiderarNovaLinha: profile.desconsiderarNovaLinha ?? base.desconsiderarNovaLinha,
    desconsiderarFastchip: profile.desconsiderarFastchip ?? base.desconsiderarFastchip,
    desconsiderarPorEntrega: profile.desconsiderarPorEntrega ?? base.desconsiderarPorEntrega,
    considerarSemDataAtivacao: profile.considerarSemDataAtivacao ?? false,
    desconsiderarAtivados: profile.desconsiderarAtivados ?? false,
    statusesEmAprovacao: normalizeAllowedContractStatuses(profile.statusesEmAprovacao),
    planIds: [...(profile.planIds ?? [])],
    desconsiderarPlanosSelecionados: profile.desconsiderarPlanosSelecionados ?? false,
  };
}

export function cloneEligibilityProfile(
  profile: UpdateCampaignEligibilityProfileDto
): UpdateCampaignEligibilityProfileDto {
  return {
    ...profile,
    allowedContractStatuses: [...(profile.allowedContractStatuses ?? [])],
    statusesEmAprovacao: [...(profile.statusesEmAprovacao ?? [])],
    planIds: [...(profile.planIds ?? [])],
  };
}

export function mapEligibilityRulesToForm(rules: CampaignEligibilityRulesDto): UpdateCampaignEligibilityRulesDto {
  const legacyProfile: Partial<CampaignEligibilityProfileDto> = {
    allowedContractStatuses: rules.allowedContractStatuses,
    incluirAditivo: rules.incluirAditivo,
    incluirRenegociacao: rules.incluirRenegociacao,
    incluirRenegociacaoInapta: !rules.exigirIsAptoRenegociacao,
    desconsiderarPortabilidade: !rules.incluirPortabilidade,
    desconsiderarNovaLinha: !rules.incluirNovaLinha,
    valorTotalMensalMin: rules.valorTotalMensalMin,
    valorTotalMensalMax: rules.valorTotalMensalMax,
  };

  return {
    usarElegibilidadeUnificada: rules.usarElegibilidadeUnificada ?? false,
    incluirEmAprovacaoNaMeta: rules.incluirEmAprovacaoNaMeta ?? false,
    meta: normalizeEligibilityProfile(rules.meta ?? legacyProfile, 'meta'),
    pontos: normalizeEligibilityProfile(rules.pontos ?? legacyProfile, 'pontos'),
  };
}

export function defaultEligibilityRulesForm(): UpdateCampaignEligibilityRulesDto {
  return {
    usarElegibilidadeUnificada: false,
    meta: defaultMetaEligibilityProfile(),
    pontos: defaultPontosEligibilityProfile(),
  };
}

export function defaultWeeklyBonusTiers(): WeeklyBonusTier[] {
  return [
    { incluirAditivo: true, valorMinimoLinha: 52.99, valorBonificacao: 20 },
    { incluirAditivo: true, valorMinimoLinha: 99.99, valorBonificacao: 40 },
  ];
}

export function defaultTop1ExtraRules() {
  return {
    receitaMinimaParticipacao: 3500,
    fonteElegibilidade: 'meta' as const,
    tipoPremio: 'premio' as const,
    valorPremiacao: undefined,
    descricaoPremio: 'Quadro do vendedor do mês, Jantar na Churrascaria e SmartWatch',
    perfilCustomizado: defaultEligibilityProfile(),
  };
}

export function defaultWeeklyCustomEligibilityProfile(): CampaignEligibilityProfileDto {
  return {
    ...sharedEligibilityStatusDefaults('meta'),
    incluirAditivo: true,
    incluirRenegociacao: false,
    considerarSemDataAtivacao: false,
    valorTotalMensalMin: 52.99,
    valorTotalMensalMax: 149.99,
    allowedContractStatuses: [
      EContractStatus.Ativo,
      EContractStatus.AguardandoEntrega,
      EContractStatus.ProcessoDeAtivacao,
      EContractStatus.BateVolta,
    ],
    statusesEmAprovacao: [],
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatYmd(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseYmd(value: string): Date | null {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function createWeeklyWindowsForPeriod(startIso: string, endIso: string): CampaignWeeklyWindow[] {
  const periodStart = parseYmd(startIso);
  const periodEnd = parseYmd(endIso);
  if (!periodStart || !periodEnd || periodEnd < periodStart) return [];

  const cursor = new Date(periodStart);
  while (cursor.getDay() !== 1) {
    cursor.setDate(cursor.getDate() - 1);
  }

  const windows: CampaignWeeklyWindow[] = [];
  let weekNumber = 0;

  while (cursor <= periodEnd) {
    const friday = new Date(cursor);
    friday.setDate(friday.getDate() + 4);
    const start = cursor > periodStart ? new Date(cursor) : new Date(periodStart);
    const end = friday < periodEnd ? friday : new Date(periodEnd);

    if (start <= end) {
      weekNumber += 1;
      windows.push({
        label: `Semana ${weekNumber}`,
        dataInicio: formatYmd(start),
        dataFim: formatYmd(end),
        horaInicio: '08:00',
        horaFim: '16:00',
        valorPremiacao: 0,
        valorPremiacaoFixo: 0,
        minLinhasAtivas: 8,
        valorMinimoLinha: 52.99,
        flags: {
          exigirProducaoTodosDiasUteis: true,
          exigirVtmeCriadoUltimoDiaAteHora: '16:00',
          exigirMax3hAguardandoCorrecaoUltimoDia: true,
        },
        tiersBonificacao: defaultWeeklyBonusTiers(),
      });
    }

    cursor.setDate(cursor.getDate() + 7);
  }

  return windows;
}

export function defaultSemanalDocument(
  campaign?: Pick<CampaignResponseDto, 'mesReferencia' | 'dataInicio' | 'dataFim' | 'periodMode'>
): CampaignWeeklyWindowsDocument {
  const { start, end } = campaign
    ? getCampaignDateRange(campaign as CampaignResponseDto)
    : (() => {
        const now = new Date();
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        return {
          start: `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-01`,
          end: `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(last)}`,
        };
      })();

  return {
    fonteElegibilidade: 'custom',
    perfilCustomizado: defaultWeeklyCustomEligibilityProfile(),
    windows: start && end ? createWeeklyWindowsForPeriod(start, end) : [],
  };
}

/** Exibe intervalo da janela semanal (data + hora, fuso local do navegador). */
export function formatWeeklyWindowRange(
  dataInicio: string,
  dataFim: string,
  horaInicio?: string,
  horaFim?: string,
): string {
  if (dataInicio.includes('T')) {
    const opts: Intl.DateTimeFormatOptions = { dateStyle: 'short', timeStyle: 'short' };
    return `${new Date(dataInicio).toLocaleString('pt-BR', opts)} — ${new Date(dataFim).toLocaleString('pt-BR', opts)}`;
  }

  const fmtDate = (iso: string) => {
    const datePart = iso.slice(0, 10);
    if (!datePart) return '—';
    const [y, m, d] = datePart.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
  };

  const hi = horaInicio?.trim().slice(0, 5) || '00:00';
  const hf = horaFim?.trim().slice(0, 5) || '23:59';
  return `${fmtDate(dataInicio)} ${hi} — ${fmtDate(dataFim)} ${hf}`;
}
